import type { EmitFn } from 'vue';
import type { GridLayout } from '@/shared/lib/pick-grid-layout';
import type {
  ImageGridFit,
  ImageGridFlow,
  ImageGridSizing,
} from '@/ui-kit/components/image-grid/types';

export type MixedPortraitGalleryKind = 'group' | 'individual';
export type ClassicPortraitGalleryOrientation = 'landscape' | 'portrait';

// The layouts of the Classic gallery. 'pages' reproduces the old carousel
// and 2x2 grid; the other five are the designs of the THE-740 review:
//   strip  one row of tiles, flipped by pages
//   decks  a row of group portraits over a row of single ones
//   focus  a stage with the active portrait beside a dock of thumbnails
//   zones  two scroll zones, groups above singles; side by side in landscape
//   feed   one vertical scroll feed
export type ClassicPortraitGalleryLayout =
  | 'pages'
  | 'strip'
  | 'decks'
  | 'focus'
  | 'zones'
  | 'feed';

export const CLASSIC_PORTRAIT_GALLERY_LAYOUTS: readonly ClassicPortraitGalleryLayout[] =
  ['pages', 'strip', 'decks', 'focus', 'zones', 'feed'];

// kiosks.classic_gallery_layout: a layout, or "legacy" for the components
// that predate the mixed gallery.
export type KioskClassicGalleryLayout = 'legacy' | ClassicPortraitGalleryLayout;

export function isClassicPortraitGalleryLayout(
  value: unknown
): value is ClassicPortraitGalleryLayout {
  return CLASSIC_PORTRAIT_GALLERY_LAYOUTS.includes(
    value as ClassicPortraitGalleryLayout
  );
}

// The name the stage-and-dock layout went by while the designs were made;
// a build that still sets it gets the same screen.
const LAYOUT_ALIASES: Record<string, ClassicPortraitGalleryLayout> = {
  rail: 'focus',
};

export function parseClassicPortraitGalleryLayout(
  value: unknown
): ClassicPortraitGalleryLayout | null {
  if (isClassicPortraitGalleryLayout(value)) {
    return value;
  }
  return typeof value === 'string' ? (LAYOUT_ALIASES[value] ?? null) : null;
}

// The kiosk flag is the only gate, and a kiosk opts in: a missing or null
// flag means no group portrait anywhere.
export function includesGroupPortraits(
  multiSubjectEnabled: boolean | null | undefined
): boolean {
  return multiSubjectEnabled === true;
}

// Which old kiosk layout the paged mode reproduces. 'carousel' and 'grid'
// borrow the legacy .ImageGallery CSS variables (item width, gaps, arrow
// size, shadow space) so they stay pixel-identical to the old galleries;
// 'image-grid' is the ui-kit ImageGrid box the old PortraitImageGrid used.
export type ClassicPortraitGalleryPagesPreset =
  | 'carousel'
  | 'grid'
  | 'image-grid';

// Paged mode reuses the ui-kit ImageGrid engine, so its geometry is the same
// tuple the legacy kiosk layouts already compute via usePortraitGridLayout.
// cellAspectRatio is only the shape a cell holds until its image loads: the
// engine then gives every cell its real ratio, so a style rendered off the
// house ratio is shown whole instead of cropped.
export interface ClassicPortraitGalleryPages {
  arrowScale: number;
  cellAspectRatio: number;
  cellHeight: number | string;
  cellRadius: string;
  cellWidth: number | string;
  fit: ImageGridFit;
  flow?: ImageGridFlow;
  gap: string;
  // The group portrait is the first cell of the first page, this many times
  // larger than a single. The page keeps its height, thus the singles of
  // that page become smaller.
  groupScale: number;
  layout: GridLayout;
  preset: ClassicPortraitGalleryPagesPreset;
  sizing: ImageGridSizing;
}

// The rail is a stack of rows of equal height, flipped by pages. A row holds
// `columns` vertical portraits side by side (fractions allowed: a 3:2 tile
// costs 2.19 of them, so 4.4 fits two per row); every tile keeps its own
// ratio and is as wide as the row height needs. `rows` rows make a page;
// the arrows flip pages and on the last page the forward arrow is "+".
export interface ClassicPortraitGalleryRail {
  columns: number;
  // Lets a row grow past `columns` so a small set fits on one page: the
  // capacity becomes what the items actually need, up to this many units.
  maxColumns?: number;
  // With sections, a kind can pack its rows to fewer units than `columns`;
  // the row then grows (up to `maxRowFill`) to fill the width. The focus
  // dock puts two groups on a row but only two singles.
  sectionColumns?: Partial<Record<MixedPortraitGalleryKind, number>>;
  // How much a row not full to `columns` may grow to fill the width, so a
  // lone tile on the last page does not balloon.
  maxRowFill?: number;
  // Where the page controls sit in landscape: a column at the rows' right
  // edge (`side`), under them (`bottom`) or left/right of them in the screen
  // margins like the old carousel's arrows (`sides`). Portrait always puts
  // them beside.
  controls: 'bottom' | 'side' | 'sides';
  gap: string;
  portraitRatio: number;
  rows: number;
}

// The engine that lays the tiles out: paged rows, native scroll zones, or a
// native vertical feed.
export type ClassicPortraitGalleryEngine = 'rows' | 'zones' | 'feed';

// How the two zones sit: stacked lanes (groups above the singles, each a
// horizontal lane) or side by side (a column that stacks the wide group
// plates beside one horizontal lane of tall cards).
export type ClassicPortraitGalleryZoneFlow = 'rows' | 'columns';

// How the group rows and the rows of singles share the pages.
//   interleave  every page opens with one group row, singles fill the rest
//   sequential  all the group rows first, then the singles
// A dock that gives a plate a row of its own wants `sequential`: the visitor
// sees every group portrait before the first page turn.
export type ClassicPortraitGallerySectionOrder = 'interleave' | 'sequential';

// One layout in one orientation, fully resolved.
export interface ClassicPortraitGalleryPlan {
  engine: ClassicPortraitGalleryEngine;
  layout: ClassicPortraitGalleryLayout;
  orientation: ClassicPortraitGalleryOrientation;
  rail: ClassicPortraitGalleryRail;
  // Group and single portraits packed into rows of their own, with a
  // caption over each section.
  sections: boolean;
  sectionOrder: ClassicPortraitGallerySectionOrder;
  // A stage shows the active portrait; the rows become its dock.
  preview: boolean;
  // The dock's share of the gallery: its width in landscape, its height in
  // portrait orientation.
  dock: string;
  // Zones: stacked lanes, or a group column beside the singles lane. On a
  // landscape screen stacked lanes squeeze tall cards to stamps.
  zoneFlow: ClassicPortraitGalleryZoneFlow;
  // Zones: the groups zone's share against the singles zone's, of the
  // height when stacked and of the width when side by side.
  zoneWeights: [number, number];
  // Zones: the widest a tile may be in its zone, in container units of
  // the zone's width, so the next one peeks in.
  peek: Record<MixedPortraitGalleryKind, string>;
  // Feed: columns of the feed and how many a group portrait spans.
  feedColumns: number;
  feedGroupSpan: number;
}

export interface GalleryEntry {
  index: number;
  item: MixedPortraitGalleryItem;
}

export const PORTRAIT_TILE_RATIO = 832 / 1216;
export const GROUP_TILE_RATIO = 3 / 2;
export const GROUP_TILE_SCALE = 1.15;

// What one 3:2 group plate costs in vertical-portrait units. A section
// capacity of exactly this holds one plate and no second one, whatever
// ratio the generated image turns out to have.
const GROUP_TILE_UNITS = GROUP_TILE_RATIO / PORTRAIT_TILE_RATIO;

const RAIL_BASE: ClassicPortraitGalleryRail = {
  columns: 4,
  controls: 'side',
  gap: '0.8rem',
  portraitRatio: PORTRAIT_TILE_RATIO,
  rows: 1,
};

const PLAN_BASE: Omit<
  ClassicPortraitGalleryPlan,
  'layout' | 'orientation' | 'rail'
> = {
  engine: 'rows',
  sections: false,
  sectionOrder: 'interleave',
  preview: false,
  dock: '26%',
  zoneFlow: 'rows',
  zoneWeights: [0.95, 2],
  peek: { group: '84cqw', individual: '76cqw' },
  feedColumns: 2,
  feedGroupSpan: 2,
};

type PlanPreset = Partial<Omit<ClassicPortraitGalleryPlan, 'rail'>> & {
  rail?: Partial<ClassicPortraitGalleryRail>;
};

// The strip is the old carousel's format: one row of tiles about as tall as
// its cards, arrows at the sides, everything in one strip. The row grows up
// to 5.2 units — a 3:2 group portrait plus three vertical ones — so the
// typical session is on screen at once. In portrait orientation the strip
// is two rows, a group leading each.
//
// The decks pack the two kinds apart at 4.4 units: two 3:2 groups (2.19
// each) or four verticals to a row. On a tall screen the decks put two
// singles to a row, which grow to fill the height. The landscape focus dock
// has three rows: one group plate at full width, then two pairs of singles.
// The portrait dock has two rows and keeps two groups to a row.
//
// The zones and the feed are native scroll and take no row capacity.
const PLANS: Record<
  ClassicPortraitGalleryLayout,
  Record<ClassicPortraitGalleryOrientation, PlanPreset>
> = {
  pages: {
    landscape: {},
    portrait: {},
  },
  strip: {
    landscape: {
      rail: { columns: 3.9, controls: 'sides', maxColumns: 5.2, rows: 1 },
    },
    portrait: { rail: { columns: 3.2, maxRowFill: 1.3, rows: 2 } },
  },
  decks: {
    landscape: {
      rail: { columns: 4.4, controls: 'sides', maxRowFill: 1.25, rows: 2 },
      sections: true,
    },
    portrait: {
      rail: { columns: 4.4, rows: 2, sectionColumns: { individual: 2.2 } },
      sections: true,
    },
  },
  focus: {
    landscape: {
      dock: '26%',
      preview: true,
      rail: {
        columns: 4.4,
        controls: 'bottom',
        gap: '0.6rem',
        maxRowFill: 2.2,
        rows: 3,
        // One group plate to a row, at the dock's full width. The landscape
        // print is the product the venue sells, so it gets the room. The
        // singles keep two to a row, which leaves two rows of them.
        sectionColumns: { group: GROUP_TILE_UNITS, individual: 2 },
      },
      sections: true,
      sectionOrder: 'sequential',
    },
    portrait: {
      dock: '46%',
      preview: true,
      rail: { columns: 4.4, gap: '0.6rem', maxRowFill: 1.25, rows: 2 },
      sections: true,
    },
  },
  zones: {
    landscape: {
      engine: 'zones',
      sections: true,
      zoneFlow: 'columns',
      zoneWeights: [0.62, 1],
    },
    portrait: { engine: 'zones', sections: true },
  },
  feed: {
    landscape: { engine: 'feed', feedColumns: 4, feedGroupSpan: 2 },
    portrait: { engine: 'feed', feedColumns: 2, feedGroupSpan: 2 },
  },
};

export interface ClassicPortraitGalleryPlanOverrides {
  preview?: boolean;
  rail?: Partial<ClassicPortraitGalleryRail> | null;
}

export function resolveGalleryPlan(
  layout: ClassicPortraitGalleryLayout,
  orientation: ClassicPortraitGalleryOrientation,
  overrides: ClassicPortraitGalleryPlanOverrides = {}
): ClassicPortraitGalleryPlan {
  const preset = PLANS[layout][orientation];
  return {
    ...PLAN_BASE,
    ...preset,
    layout,
    orientation,
    preview: overrides.preview ?? preset.preview ?? PLAN_BASE.preview,
    rail: { ...RAIL_BASE, ...preset.rail, ...overrides.rail },
  };
}

// The shape a group portrait takes in the store. It lived beside the group
// step until that step went away; the gallery is its only reader now.
export interface GroupPortraitLike {
  aspectRatio?: number | null;
  error?: unknown;
  isError?: boolean;
  isGenerated?: boolean;
  isGroupPortrait?: boolean;
  isPreloaded?: boolean;
  isRetrying?: boolean;
  renderKey?: string;
  retryCount: number;
  url?: string | null;
  uuid: string;
}

export interface MixedPortraitGalleryItem {
  aspectRatio?: number | null;
  isError?: boolean;
  isGenerated?: boolean;
  isPreloaded?: boolean;
  isRetrying?: boolean;
  kind: MixedPortraitGalleryKind;
  renderKey?: string;
  retryCount?: number;
  source: unknown;
  url?: string | null;
  uuid: string;
}

export type ClassicPortraitGalleryFilter = 'all' | MixedPortraitGalleryKind;

export function filterGalleryItems<
  T extends { kind: MixedPortraitGalleryKind },
>(items: T[], filter: ClassicPortraitGalleryFilter): T[] {
  return filter === 'all'
    ? items
    : items.filter((item) => item.kind === filter);
}

export function galleryKindCounts<T extends { kind: MixedPortraitGalleryKind }>(
  items: T[]
): Record<MixedPortraitGalleryKind, number> {
  const counts = { group: 0, individual: 0 };
  for (const item of items) {
    counts[item.kind] += 1;
  }
  return counts;
}

// One from the first list, one from the second, and so on; what is left
// over goes last. The feed alternates a row led by a group with a row of
// singles this way, instead of every group stacked at the top.
export function alternate<T>(first: T[], second: T[]): T[] {
  const ordered: T[] = [];
  const turns = Math.max(first.length, second.length);
  for (let turn = 0; turn < turns; turn += 1) {
    const a = first[turn];
    if (a !== undefined) {
      ordered.push(a);
    }
    const b = second[turn];
    if (b !== undefined) {
      ordered.push(b);
    }
  }
  return ordered;
}

export interface PackedRow<T> {
  free: number;
  items: T[];
}

// First-fit packing: an item goes into the first row that still has room for
// it, so a narrow tile fills the room a wide one left in an earlier row.
export function packGalleryRows<T>(
  items: T[],
  unitsOf: (item: T) => number,
  capacity: number
): PackedRow<T>[] {
  return packMixedGalleryRows(items, unitsOf, capacity, () => false);
}

// A group portrait opens each row and the individuals fill in behind it:
// the page the team agreed on is one group plus three portraits, and a
// plain first-fit over "groups first, then individuals" would put two
// groups side by side instead.
export function packMixedGalleryRows<T>(
  items: T[],
  unitsOf: (item: T) => number,
  capacity: number,
  leads: (item: T) => boolean
): PackedRow<T>[] {
  const rows: PackedRow<T>[] = items
    .filter(leads)
    .map((item) => ({ free: capacity - unitsOf(item), items: [item] }));
  for (const item of items) {
    if (leads(item)) {
      continue;
    }
    const units = unitsOf(item);
    const row = rows.find((candidate) => candidate.free + 1e-6 >= units);
    if (row) {
      row.items.push(item);
      row.free -= units;
    } else {
      rows.push({ free: capacity - units, items: [item] });
    }
  }
  return rows;
}

// Pages are rows, so a set that overflows one row is split as evenly as
// the tiles allow: 8 verticals become 4 + 4 rather than 5 + 3, and the
// second page keeps the first page's tile size. When the even split does
// not pack into the planned number of rows, the widest row wins instead.
export function balancedRowCapacity(
  units: number,
  columns: number,
  maxColumns: number,
  fits: (capacity: number) => number
): number {
  if (units <= maxColumns) {
    return Math.max(columns, units);
  }
  const planned = Math.ceil(units / maxColumns);
  const target = Math.max(columns, units / planned);
  return fits(target) <= planned ? target : maxColumns;
}

export function mixedItemRatio(item: MixedPortraitGalleryItem): number {
  const ratio = Number(item.aspectRatio);
  if (Number.isFinite(ratio) && ratio > 0) {
    return ratio;
  }
  return item.kind === 'group' ? GROUP_TILE_RATIO : PORTRAIT_TILE_RATIO;
}

// The messages of a tile live in the Portraits namespace, for the group too.
// PortraitsGroup keeps only the label of the "+" button of the galleries.
export function mixedItemCopyKey(
  item: MixedPortraitGalleryItem,
  key: string
): string {
  if (item.kind === 'group' && key === 'title') {
    return 'Portraits.stageKindGroup';
  }
  return `Portraits.${key}`;
}

export function isMixedItemReady(item: MixedPortraitGalleryItem): boolean {
  return Boolean(item.isPreloaded && item.url && !item.isError);
}

export interface PortraitRatioSource {
  actualRatio?: number;
  aspectRatio?: number | null;
  isPreloaded?: boolean;
}

// The ratio a portrait's tile takes: what its image measured on screen, else
// what the store learned when it preloaded, else the ratio a loaded portrait
// carries. A portrait that has not loaded yet takes the kiosk's portrait
// format, so its placeholder already has the shape of the tile it becomes
// and the page does not shift when the image arrives.
export function individualTileRatio(
  portrait: PortraitRatioSource,
  measured: number | undefined,
  kioskPortraitRatio: number
): number {
  return (
    measured ??
    portrait.actualRatio ??
    (portrait.isPreloaded ? portrait.aspectRatio : undefined) ??
    kioskPortraitRatio
  );
}

// The paged grid shows a 1x1 placeholder gif until a portrait is preloaded,
// and it fires the same load event as the portrait. Only a ready item's own
// image measures its tile; the placeholder's 1:1 would make the cell square.
export function loadedMixedItemRatio(
  item: MixedPortraitGalleryItem,
  image: Pick<HTMLImageElement, 'naturalHeight' | 'naturalWidth'> | null
): number | null {
  if (
    !(
      image &&
      isMixedItemReady(item) &&
      image.naturalWidth > 1 &&
      image.naturalHeight > 1
    )
  ) {
    return null;
  }
  return image.naturalWidth / image.naturalHeight;
}

// The singles and the group portrait stop at the same retry count.
export function canRetryMixedItem(item: MixedPortraitGalleryItem): boolean {
  return !item.isRetrying && (item.retryCount ?? 0) < 3;
}

export interface GalleryTileSource {
  activeUuid: string | null;
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  selectedUuids: string[];
  selectionOnTile: boolean;
}

export type GalleryTileEmit = EmitFn<{
  activate: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  tryAgain: [item: MixedPortraitGalleryItem];
}>;

export interface GalleryTileProps {
  item: MixedPortraitGalleryItem;
  index: number;
  active: boolean;
  selected: boolean;
  selectionOnTile: boolean;
  count: number | undefined;
  onActivate: (event: MouseEvent) => void;
  onItemLoad: (event: Event) => void;
  onTryAgain: () => void;
  onCountDecrement: (event: MouseEvent) => void;
  onCountIncrement: (event: MouseEvent) => void;
}

export interface GalleryTileBindings {
  tileBindings: (entry: GalleryEntry) => GalleryTileProps;
  tileKey: (entry: GalleryEntry) => string;
}

// The key stays out of the bindings: a key only in a v-bind object makes the
// v-for an unkeyed fragment.
export function galleryTileBindings(
  props: GalleryTileSource,
  emit: GalleryTileEmit
): GalleryTileBindings {
  return {
    tileBindings: ({ item, index }: GalleryEntry) => ({
      item,
      index,
      active: item.uuid === props.activeUuid,
      selected: props.selectedUuids.includes(item.uuid),
      selectionOnTile: props.selectionOnTile,
      count: props.getSelectedPortraitCount(item),
      onActivate: (event: MouseEvent) => emit('activate', item, index, event),
      onItemLoad: (event: Event) => emit('itemLoad', item, index, event),
      onTryAgain: () => emit('tryAgain', item),
      onCountDecrement: (event: MouseEvent) =>
        emit('countDecrement', event, item),
      onCountIncrement: (event: MouseEvent) =>
        emit('countIncrement', event, item),
    }),
    tileKey: ({ item }: GalleryEntry) =>
      item.renderKey ?? `${item.kind}:${item.uuid}`,
  };
}
